<full-screen-loading text="Signing you in" *ngIf="view === 'loading'"></full-screen-loading>

<main class="security-page" *ngIf="view !== 'loading'">
    <section class="security-card" aria-live="polite">
        <img class="shuffll-logo" src="/assets/images/brand/shuffll_horz-logo_dark.svg" alt="Shuffll" />

        <ng-container *ngIf="view === 'method-choice'">
            <div class="security-mark" aria-hidden="true"><mat-icon>verified_user</mat-icon></div>
            <p class="eyebrow">Organization security</p>
            <h1>{{ enrollmentRequired ? 'Protect your account' : 'Confirm it’s you' }}</h1>
            <p class="intro">
                {{
                    enrollmentRequired
                        ? 'Your organization requires a stronger sign-in. Set up either method to continue.'
                        : methods.length > 1
                          ? 'Choose a verification method to finish signing in.'
                          : 'Use your saved verification method to finish signing in.'
                }}
            </p>

            <div class="method-rail">
                <button
                    type="button"
                    class="method-card"
                    *ngIf="enrollmentRequired || methods.includes(StrongAuthMethod.Passkey)"
                    [disabled]="isBusy || !passkeys.isSupported()"
                    (click)="usePasskeyAsync()">
                    <span class="method-icon"><mat-icon>passkey</mat-icon></span>
                    <span class="method-copy">
                        <strong>{{
                            enrollmentRequired
                                ? 'Set up a passkey'
                                : passkeyRetryAvailable
                                  ? 'Retry passkey'
                                  : 'Use a passkey'
                        }}</strong>
                        <small>Face, fingerprint, or device PIN</small>
                    </span>
                    <mat-icon class="method-arrow">arrow_forward</mat-icon>
                </button>

                <div class="either" *ngIf="enrollmentRequired || methods.length > 1"><span>or</span></div>

                <button
                    type="button"
                    class="method-card"
                    *ngIf="enrollmentRequired || methods.includes(StrongAuthMethod.Totp)"
                    [disabled]="isBusy"
                    (click)="useAuthenticatorAsync()">
                    <span class="method-icon"><mat-icon>phonelink_lock</mat-icon></span>
                    <span class="method-copy">
                        <strong>{{
                            enrollmentRequired ? 'Set up an authenticator app' : 'Use an authenticator app'
                        }}</strong>
                        <small>Enter a rotating 6-digit code</small>
                    </span>
                    <mat-icon class="method-arrow">arrow_forward</mat-icon>
                </button>
            </div>
            <p class="availability-note" *ngIf="!passkeys.isSupported() && (enrollmentRequired || methods.includes(StrongAuthMethod.Passkey))">
                Passkeys are unavailable in this browser.
                {{ enrollmentRequired ? 'Set up an authenticator app instead.' : methods.includes(StrongAuthMethod.Totp) ? 'Use your authenticator app instead.' : 'Open the recovery help below.' }}
            </p>
        </ng-container>

        <ng-container *ngIf="view === 'totp-code' || view === 'totp-enrollment'">
            <button type="button" class="back-button" (click)="returnToMethods()" [disabled]="isBusy">
                <mat-icon>arrow_back</mat-icon> Back
            </button>
            <p class="eyebrow">Authenticator app</p>
            <h1>{{ view === 'totp-enrollment' ? 'Scan, then confirm' : 'Enter your code' }}</h1>
            <p class="intro" *ngIf="view === 'totp-enrollment'">
                Scan this code with Google Authenticator, Microsoft Authenticator, 1Password, or another compatible app.
            </p>
            <p class="intro" *ngIf="view === 'totp-code'">Enter the current code from your authenticator app.</p>

            <div class="qr-wrap" *ngIf="view === 'totp-enrollment'">
                <img [src]="qrCodeDataUrl" alt="Authenticator setup QR code" />
                <details>
                    <summary>Can’t scan it?</summary>
                    <code>{{ totpEnrollment?.secret }}</code>
                </details>
            </div>

            <form class="code-form" (ngSubmit)="submitTotpAsync()">
                <label for="totp-code">6-digit code</label>
                <input
                    id="totp-code"
                    name="totpCode"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="6"
                    pattern="[0-9]{6}"
                    [(ngModel)]="totpCode"
                    (paste)="pasteTotpCode($event)"
                    [disabled]="isBusy"
                    autofocus />
                <button mat-raised-button color="primary" type="submit" [disabled]="isBusy || totpCode.length !== 6">
                    <mat-spinner diameter="18" *ngIf="isBusy"></mat-spinner>
                    <span>{{ view === 'totp-enrollment' ? 'Finish setup' : 'Verify and sign in' }}</span>
                </button>
            </form>
        </ng-container>

        <ng-container *ngIf="view === 'error'">
            <div class="security-mark error" aria-hidden="true"><mat-icon>lock_clock</mat-icon></div>
            <p class="eyebrow">Sign-in interrupted</p>
            <h1>Start a fresh sign-in</h1>
            <p class="intro">{{ errorMessage }}</p>
            <a mat-raised-button color="primary" routerLink="/login">Return to sign in</a>
        </ng-container>

        <p class="inline-error" *ngIf="errorMessage && view !== 'error'" role="alert">{{ errorMessage }}</p>

        <section
            class="sign-in-help"
            aria-labelledby="browser-help-title"
            *ngIf="view !== 'error' && (enrollmentRequired || methods.includes(StrongAuthMethod.Passkey))">
            <h2 id="browser-help-title">Passkey not working?</h2>
            <p>
                If you’re using an in-app browser, continue in Chrome, Edge, or Safari on the device where you
                saved your passkey. Copy this sign-in link and paste it into that browser’s address bar.
            </p>
            <p *ngIf="!enrollmentRequired">
                If your passkey is on your phone, choose another device in the browser’s passkey prompt and
                follow its instructions.
            </p>
            <button mat-stroked-button color="primary" type="button" [disabled]="isBusy" (click)="copyBrowserSignInLinkAsync()">
                Copy sign-in link
            </button>
            <p *ngIf="browserStatusMessage" role="status">{{ browserStatusMessage }}</p>
            <ng-container *ngIf="showBrowserSignInLink">
                <label for="browser-sign-in-link">Sign-in link</label>
                <input
                    #browserSignInLink
                    id="browser-sign-in-link"
                    class="browser-sign-in-link"
                    type="text"
                    readonly
                    [value]="browserSignInUrl"
                    (focus)="browserSignInLink.select()"
                    (click)="browserSignInLink.select()" />
            </ng-container>
        </section>

        <section class="sign-in-help" aria-labelledby="recovery-help-title" *ngIf="view !== 'error' && !enrollmentRequired">
            <h2 id="recovery-help-title">Can’t use your verification method?</h2>
            <p *ngIf="methods.includes(StrongAuthMethod.Totp)">
                You can also use the authenticator app already set up for this account.
            </p>
            <p>
                Ask your organization owner or manager to approve account recovery. Once they confirm,
                check your recovery status here.
            </p>
            <button mat-stroked-button color="primary" type="button" [disabled]="isBusy" (click)="refreshVerificationAsync()">
                {{ isBusy ? 'Checking…' : 'Check recovery status' }}
            </button>
            <p *ngIf="recoveryStatusMessage" role="status">{{ recoveryStatusMessage }}</p>
        </section>

        <nav class="sign-in-actions" aria-label="Sign-in help" *ngIf="view !== 'error'">
            <a href="mailto:support@shuffll.com?subject=Help%20with%20sign-in">Contact Shuffll support</a>
            <a routerLink="/login" *ngIf="!isBusy">Sign in again or use a different account</a>
        </nav>
        <p class="availability-note" *ngIf="view !== 'error'">Need access to this workspace? Use your own email and ask a workspace manager to invite you.</p>
        <p class="security-note" *ngIf="view !== 'error'">
            <mat-icon>lock</mat-icon>
            {{
                enrollmentRequired || methods.length > 1
                    ? 'Either method satisfies your organization’s policy.'
                    : 'This method satisfies your organization’s policy.'
            }}
        </p>
    </section>
</main>
